import { node } from 'prop-types';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

import { leisureFormDefaultValues } from 'constants';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { setLeisureSteps, updateCurrentLeisureStepValidity } from 'store';

const LeisurePurchaseContext = createContext();

const LeisurePurchaseProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const leisureSteps = useSelector((state) => state.leisurePurchase.steps);
  const { currentUser } = useUser();
  const { call: userCall, data: user, loading: userLoading } = useFetch();

  const currentStep = useMemo(() => leisureSteps?.find((step) => step.current), [leisureSteps]);

  /**
   * Form to manage the leisure purchase
   */
  const leisurePurchaseForm = useForm({
    mode: 'onBlur',
    defaultValues: { ...leisureFormDefaultValues }
  });

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    /* Trigger effect only on leisure purchase routes */
    if (location.pathname.includes(routes.leisurePurchase.path)) {
      const targetStep = leisureSteps.find((step) => step.path === location.pathname);

      /**
       * Update currentStep.current and targetStep.current if the conditions are fulfilled
       * If the target step is after currentStep,
       * then all previous steps of the target step should be valid and completed
       * Navigation to previous steps is allowed without verification
       */
      if (targetStep?.order > currentStep?.order) {
        const allPreviousStepsValid = leisureSteps.slice(0, targetStep.order).every((step) => step.valid === true);
        if (allPreviousStepsValid) {
          dispatch(setLeisureSteps({ stepToUpdate: currentStep, targetStep }));
        } else {
          navigateToStep(currentStep.slug);
        }
      } else if (targetStep?.order < currentStep?.order) {
        dispatch(setLeisureSteps({ stepToUpdate: currentStep, targetStep }));
      }
    }
  }, [location]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getUser = () => {
    userCall({
      service: 'castelis',
      path: `/users/${currentUser.serialNumber}`,
      method: 'get'
    });
  };

  /**
   * @description Method to navigate to the next step on click on the next button
   * Navigation from stepper click is handled on Step Components
   */
  const next = async () => {
    if (!isFormValid()) return;
    // Update the current step in the store
    dispatch(updateCurrentLeisureStepValidity({ valid: true, completed: true }));

    // Navigate to next step
    const nextStep = leisureSteps.find((step) => step.order === currentStep.order + 1);
    navigateToStep(nextStep.slug);
  };

  /**
   * @description Method to navigate to the previous step on click on the previous button
   * Navigation from stepper click is handled on Step Components
   */
  const prev = () => {
    dispatch(updateCurrentLeisureStepValidity({ valid: isFormValid(), completed: isFormValid() }));
    // Return to leisure results page if we are on the first step
    if (currentStep?.order === 0) {
      navigate({
        pathname: routes.results.path,
        search: createSearchParams({ type: 'leisures' }).toString()
      });
    } else {
      // Navigate to previous step
      const previousStep = leisureSteps.find((step) => step.order === currentStep.order - 1);
      if (previousStep) navigateToStep(previousStep.slug);
    }
  };

  const isFormValid = () => {
    switch (currentStep.order) {
      case 0:
        // Summary step is always valid
        return true;
      default:
        return leisurePurchaseForm.formState.isValid;
    }
  };

  const navigateToStep = (stepSlug) => {
    const path = routes.leisurePurchase.children[stepSlug].path;
    navigate(path);
  };

  return (
    <LeisurePurchaseContext.Provider
      value={{
        user,
        userLoading,
        // Stepper
        next,
        prev,
        currentStep,
        navigateToStep
      }}
    >
      <FormProvider {...leisurePurchaseForm}>{children}</FormProvider>
    </LeisurePurchaseContext.Provider>
  );
};

export const useLeisurePurchaseContext = () => useContext(LeisurePurchaseContext);

LeisurePurchaseProvider.propTypes = {
  children: node
};

export default LeisurePurchaseProvider;
